import { useId, useRef, useState, type FormEvent } from "react";
import { useNativeDialog } from "../../components/useNativeDialog";
import { ApiError } from "../../lib/api-client";
import { updateWish } from "../../lib/api-wish";
import type { OwnerWish, WishPriority } from "../../types/wish";
import { OGPreview } from "./OGPreview";
import { WishEventPicker } from "./WishEventPicker";

interface EditWishModalProps {
  wish: OwnerWish;
  onClose: () => void;
  onUpdated: (wish: OwnerWish) => void;
}

const priorities: ReadonlyArray<{ value: WishPriority; label: string }> = [
  { value: "high", label: "Высокий" },
  { value: "medium", label: "Средний" },
  { value: "low", label: "Низкий" },
];

function validURL(value: string): boolean {
  if (!/^https?:\/\//i.test(value)) return false;
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
}

function mutationError(error: unknown): string {
  if (error instanceof ApiError && error.code === "FORBIDDEN") {
    return "Нельзя редактировать чужое желание";
  }
  if (error instanceof ApiError && error.code === "NOT_FOUND") {
    return "Желание больше не существует";
  }
  if (error instanceof ApiError && error.code === "VALIDATION_FAILED") {
    return "Проверьте заполненные поля";
  }
  return "Не удалось сохранить изменения. Попробуйте ещё раз.";
}

export function EditWishModal({ wish, onClose, onUpdated }: EditWishModalProps) {
  const titleId = useId();
  const urlId = useId();
  const descriptionId = useId();
  const errorId = useId();
  const [title, setTitle] = useState(wish.title);
  const [url, setURL] = useState(wish.url ?? "");
  const [description, setDescription] = useState(wish.description ?? "");
  const [priority, setPriority] = useState<WishPriority>(wish.priority);
  const [eventID, setEventID] = useState(wish.event?.id ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const modal = useNativeDialog(submitting, onClose, titleInputRef);

  const trimmedTitle = title.trim();
  const trimmedURL = url.trim();
  const canSubmit = trimmedTitle.length > 0 && !submitting;

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!canSubmit) return;
    if (trimmedURL && !validURL(trimmedURL)) {
      setError("Ссылка должна начинаться с http:// или https://");
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const initialEventID = wish.event?.id ?? "";
      const updated = await updateWish(wish.id, {
        title: trimmedTitle,
        description: description.trim() || null,
        url: trimmedURL || null,
        priority,
        ...(eventID !== initialEventID
          ? { event_id: eventID || null }
          : {}),
      });
      onUpdated(updated);
    } catch (mutationFailure) {
      setError(mutationError(mutationFailure));
      setSubmitting(false);
    }
  }

  const inputClass =
    "form-control h-12 w-full rounded-2xl border border-line bg-white px-4 focus:outline-none disabled:cursor-not-allowed";
  const labelClass = "mb-2 block text-[13px] font-bold text-content";

  return (
    <dialog
      ref={modal.ref}
      onCancel={modal.onCancel}
      onKeyDown={modal.onKeyDown}
      aria-labelledby={`${titleId}-dialog`}
      aria-describedby={error ? errorId : undefined}
      className="m-auto max-h-[94vh] w-full max-w-xl overflow-y-auto overscroll-contain rounded-[28px] border-0 bg-white p-5 text-content shadow-2xl backdrop:bg-black/40 max-sm:mb-0 max-sm:mt-auto max-sm:rounded-b-none sm:p-7"
    >
      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-action">Желание</p>
          <h2 id={`${titleId}-dialog`} className="mt-1 text-2xl font-extrabold tracking-[-0.04em] text-[#171714]">
            Редактировать
          </h2>
        </div>
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          aria-label="Закрыть редактирование"
          className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-surface-subtle text-xl text-content disabled:cursor-not-allowed"
        >
          ×
        </button>
      </div>

      <form onSubmit={submit} noValidate className="space-y-5">
        {error && (
          <div id={errorId} role="alert" className="rounded-2xl border border-[#FECACA] bg-danger-soft px-4 py-3 text-sm font-semibold text-danger">
            {error}
          </div>
        )}

        <div>
          <label htmlFor={titleId} className={labelClass}>Название *</label>
          <input
            ref={titleInputRef}
            id={titleId}
            value={title}
            onChange={(event) => {
              setTitle(event.target.value);
              setError(null);
            }}
            maxLength={500}
            disabled={submitting}
            aria-invalid={error !== null}
            aria-describedby={error ? errorId : undefined}
            className={inputClass}
          />
        </div>

        <div>
          <label htmlFor={urlId} className={labelClass}>Ссылка (опционально)</label>
          <input
            id={urlId}
            type="url"
            value={url}
            onChange={(event) => {
              setURL(event.target.value);
              setError(null);
            }}
            maxLength={2048}
            disabled={submitting}
            placeholder="https://..."
            className={inputClass}
          />
          <OGPreview url={trimmedURL} imageUrl={wish.image_url ?? undefined} title={wish.title} />
        </div>

        <div>
          <label htmlFor={descriptionId} className={labelClass}>Описание (опционально)</label>
          <textarea
            id={descriptionId}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            maxLength={2000}
            rows={3}
            disabled={submitting}
            className="form-control w-full resize-none rounded-2xl border border-line bg-white px-4 py-3 focus:outline-none disabled:cursor-not-allowed"
          />
        </div>

        <fieldset disabled={submitting}>
          <legend className={labelClass}>Приоритет</legend>
          <div role="radiogroup" className="flex gap-1 rounded-2xl bg-surface-subtle p-1">
            {priorities.map((option) => (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={priority === option.value}
                onClick={() => setPriority(option.value)}
                className={`flex-1 cursor-pointer rounded-xl px-3 py-3 text-sm font-semibold ${
                  priority === option.value ? "bg-content text-white" : "text-muted hover:bg-white"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </fieldset>

        <WishEventPicker
          value={eventID}
          onChange={(nextEventID) => {
            setEventID(nextEventID);
            setError(null);
          }}
          disabled={submitting}
          currentEvent={wish.event}
        />

        <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:justify-end">
            <button type="button" onClick={onClose} disabled={submitting} className="h-12 cursor-pointer rounded-xl border border-line px-6 text-sm font-bold text-content disabled:cursor-not-allowed">
            Отмена
          </button>
            <button type="submit" disabled={!canSubmit} className="h-12 cursor-pointer rounded-xl bg-action px-7 text-sm font-bold text-white disabled:cursor-not-allowed disabled:bg-surface-disabled">
            {submitting ? "Сохраняем…" : "Сохранить"}
          </button>
        </div>
      </form>
    </dialog>
  );
}
